import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface ProfessorScheduleEntry {
  id: string;
  member_id: string;
  professor_id: string;
  gym_id: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_active: boolean;
  member?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    avatar_url: string | null;
  };
}

export const useProfessorSchedules = (gymId?: string | null) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["professor-schedules", user?.id, gymId],
    queryFn: async (): Promise<ProfessorScheduleEntry[]> => {
      const { data, error } = await supabase
        .from("member_schedules")
        .select("*")
        .eq("professor_id", user!.id)
        .eq("is_active", true)
        .order("day_of_week")
        .order("start_time");

      if (error) throw error;

      const memberIds = [...new Set((data || []).map(s => s.member_id))];
      if (memberIds.length === 0) return [];

      const { data: members } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, avatar_url")
        .in("id", memberIds);

      const memberMap = new Map((members || []).map(m => [m.id, m]));

      return (data || []).map(s => ({
        ...s,
        member: memberMap.get(s.member_id) || undefined,
      }));
    },
    enabled: !!user && !!gymId,
  });
};
